import React, { useMemo, useState } from 'react'
import Spinner from 'react-bootstrap/Spinner'

const DEFAULT_SORT = {
  key: 'botCount',
  direction: 'desc',
}

const getSeriesNumber = row => {
  const explicitNumber = Number(row?.seriesNumber)
  if (Number.isInteger(explicitNumber)) {
    return explicitNumber
  }

  const name = `${row?.leagueLevelUnitName || ''}`.trim()
  const match = name.match(/\.(\d+)$/)
  if (match) {
    const parsed = Number.parseInt(match[1], 10)
    if (Number.isInteger(parsed)) {
      return parsed
    }
  }

  const fallbackId = Number(row?.leagueLevelUnitID)
  return Number.isInteger(fallbackId) ? fallbackId : 0
}

const getSortValue = (row, key) => {
  if (key === 'seriesNumber') {
    return getSeriesNumber(row)
  }

  const numericValue = Number(row?.[key])
  return Number.isFinite(numericValue) ? numericValue : Number.NEGATIVE_INFINITY
}

const compareRows = (left, right, sortConfig) => {
  const leftValue = getSortValue(left, sortConfig.key)
  const rightValue = getSortValue(right, sortConfig.key)

  if (leftValue < rightValue) {
    return sortConfig.direction === 'asc' ? -1 : 1
  }

  if (leftValue > rightValue) {
    return sortConfig.direction === 'asc' ? 1 : -1
  }

  const leftName = `${left?.leagueLevelUnitName || ''}`
  const rightName = `${right?.leagueLevelUnitName || ''}`
  return leftName.localeCompare(rightName)
}

const formatWholeNumber = value => {
  const numericValue = Number(value)

  if (!Number.isFinite(numericValue)) {
    return value ?? '-'
  }

  return `${Math.round(numericValue)}`
}

const SortableHeader = ({ label, columnKey, sortConfig, onSort }) => {
  const isActive = sortConfig.key === columnKey
  const directionLabel = isActive ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''

  return (
    <th>
      <button type="button" className="headerSortButton" onClick={() => onSort(columnKey)}>
        <span>{label}</span>
        <span className="sortArrow">{directionLabel}</span>
      </button>
    </th>
  )
}

const BotSeriesTable = ({ isLoading, rows }) => {
  const [sortConfig, setSortConfig] = useState(DEFAULT_SORT)

  const sortedRows = useMemo(() => {
    return [...(Array.isArray(rows) ? rows : [])].sort((left, right) => compareRows(left, right, sortConfig))
  }, [rows, sortConfig])

  const handleSort = columnKey => {
    setSortConfig(current => {
      if (current.key === columnKey) {
        return {
          key: columnKey,
          direction: current.direction === 'asc' ? 'desc' : 'asc',
        }
      }

      return {
        key: columnKey,
        direction: columnKey === 'botCount' ? 'desc' : 'asc',
      }
    })
  }

  return (
    <section className="tableWrap">
      {isLoading ? (
        <div className="loadingState" role="status" aria-live="polite">
          <Spinner animation="border" variant="light" />
          <span>Loading bot series...</span>
        </div>
      ) : (
        <table>
          <thead>
            <tr>
              <SortableHeader label="Series" columnKey="seriesNumber" sortConfig={sortConfig} onSort={handleSort} />
              <SortableHeader label="League rank" columnKey="leagueRank" sortConfig={sortConfig} onSort={handleSort} />
              <SortableHeader label="Bots" columnKey="botCount" sortConfig={sortConfig} onSort={handleSort} />
              <SortableHeader label="Avg rank" columnKey="averageRank" sortConfig={sortConfig} onSort={handleSort} />
              <SortableHeader label="Avg hatstats" columnKey="averageHatstats" sortConfig={sortConfig} onSort={handleSort} />
            </tr>
          </thead>
          <tbody>
            {sortedRows.length ? sortedRows.map(row => (
              <tr key={row.leagueLevelUnitID}>
                <td>
                  {row.seriesUrl ? (
                    <a href={row.seriesUrl} target="_blank" rel="noreferrer">
                      {row.leagueLevelUnitName || row.leagueLevelUnitID}
                    </a>
                  ) : (
                    row.leagueLevelUnitName || row.leagueLevelUnitID
                  )}
                </td>
                <td>{formatWholeNumber(row.leagueRank)}</td>
                <td>{Number.isFinite(Number(row.botCount)) ? Number(row.botCount) : '-'}</td>
                <td>{formatWholeNumber(row.averageRank)}</td>
                <td>{formatWholeNumber(row.averageHatstats)}</td>
              </tr>
            )) : (
              <tr>
                <td colSpan="5">No bot series found.</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </section>
  )
}

export default BotSeriesTable
